<script setup lang="ts">
import { onMounted, ref, computed } from 'vue';
import { useMaintenanceStore } from '@/store/maintenance';
import { useUserStore } from '@/store/user';
import { useI18n } from 'vue-i18n';
import { storeToRefs } from 'pinia';
import { IMaintenance } from '@/interfaces/entities/maintenance';
import { IMaintenanceComment } from '@/interfaces/entities/maintenance_comment';
import ConfirmationModal from '@/components/Globals/ConfirmationModal.vue';
import MaintenanceSection from './UserMaintenance/MaintenanceSection.vue';
import MaintenanceModal from './UserMaintenance/MaintenanceModal.vue';


const { t } = useI18n();
const maintenanceStore = useMaintenanceStore();
const { getUserMaintenances } = maintenanceStore;
const { listUserMaintenances } = storeToRefs(maintenanceStore);
const {
  getMaintenanceComments,
  createMaintenanceComment,
  updateMaintenanceComment,
  deleteMaintenanceComment,
  destroyMaintenance,
} = maintenanceStore;

const maintenances = computed(() => {
  return {
    Open: listUserMaintenances.value
      .filter(m => m.status === 'open')
      .sort((a, b) => new Date(b.created_at).getTime() - new Date(a.created_at).getTime()),
    InProgress: listUserMaintenances.value
      .filter(m => m.status === 'in_progress')
      .sort((a, b) => new Date(b.updated_at).getTime() - new Date(a.updated_at).getTime()),
    Finished: listUserMaintenances.value
      .filter((m) => m.status === 'done' && !m.archived)
      .sort((a, b) => new Date(b.updated_at).getTime() - new Date(a.updated_at).getTime()),
  };
});

const selectedMaintenance = ref<IMaintenance | null>(null);
const actionsTaken = ref<string>('');
const selectedSection = ref<string>('');
const scheduledForDate = ref<Date | null>(null);
const selectedMaintenanceTypeId = ref<string | ''>('');

const comments = ref<IMaintenanceComment[]>([]);
const newCommentText = ref<string>('');
const editingCommentId = ref<number | null>(null);
const currentTab = ref<'details' | 'comments'>('details');

const commentsLimit = ref(3)

const loadComments = async (maintenanceId: number) => {
  const result = await getMaintenanceComments(
    1,
    commentsLimit.value,
    "created_at",
    "desc",
    String(maintenanceId)
  )
  comments.value = result?.data || []
}

const loadMoreComments = async () => {
  commentsLimit.value += 3
  if (selectedMaintenance.value) {
    await loadComments(selectedMaintenance.value.id)
  }
}

const addComment = async () => {
  if (newCommentText.value.trim() !== '' && selectedMaintenance.value) {
    await createMaintenanceComment({
      maintenance_id: selectedMaintenance.value.id,
      comment: newCommentText.value,
      user_id: useUserStore().getCurrentUser.id
    });
    await loadComments(selectedMaintenance.value.id);
    newCommentText.value = '';
  }
};

const startEditingComment = (comment: IMaintenanceComment) => {
  editingCommentId.value = comment.id;
  newCommentText.value = comment.comment;
};

const editComment = async () => {
  if (editingCommentId.value !== null && newCommentText.value.trim() !== '' && selectedMaintenance.value) {
    await updateMaintenanceComment(editingCommentId.value, {
      comment: newCommentText.value
    });
    await loadComments(selectedMaintenance.value.id);
    newCommentText.value = '';
    editingCommentId.value = null;
  }
};

const showDeleteConfirm = ref(false);
const commentToDelete = ref<number | null>(null);

const confirmDeleteComment = (commentId: number) => {
  commentToDelete.value = commentId;
  showDeleteConfirm.value = true;
};

const cancelDelete = () => {
  showDeleteConfirm.value = false;
  commentToDelete.value = null;
};

const confirmDelete = async () => {
  if (commentToDelete.value !== null && selectedMaintenance.value) {
    await deleteMaintenanceComment(commentToDelete.value);
    await loadComments(selectedMaintenance.value.id);
  }
  cancelDelete();
};

const cancelEditing = () => {
  editingCommentId.value = null;
  newCommentText.value = '';
};

const selectMaintenance = (maintenance: IMaintenance, section: string) => {
  selectedMaintenance.value = maintenance;
  selectedSection.value = section;
  actionsTaken.value = maintenance.actions_taken || '';
  scheduledForDate.value = section === 'open' ? new Date() : maintenance.scheduled_for ? new Date(maintenance.scheduled_for) : null;
  currentTab.value = 'details';
  if (maintenance.id) {
    loadComments(maintenance.id);
  }
};

const closeModal = () => {
  selectedMaintenance.value = null;
  selectedSection.value = '';
  comments.value = [];
  newCommentText.value = '';
  editingCommentId.value = null;
};

const onMaintenanceUpdated = (updated: IMaintenance) => {
  selectedMaintenance.value = updated;
  getUserMaintenances();
};

const startMaintenance = async (id: number) => {
  const maintenance = maintenances.value.Open.find((m) => m.id === id);
  if (maintenance) {
    await maintenanceStore.updateMaintenance(id, {
      status: 'in_progress',
      actions_taken: '',
      maintenance_type_id: selectedMaintenanceTypeId.value || null,
      scheduled_for: ''
    });
    getUserMaintenances();
    closeModal();
  }
};

const finishedMaintenance = async (id: number) => {
  await maintenanceStore.updateMaintenance(id, {
    status: 'done',
    actions_taken: actionsTaken.value,
    scheduled_for: '',
    maintenance_type_id: null
  });
  getUserMaintenances();
  closeModal();
};

const handleDestroyMaintenance = async (id: number) => {
  await destroyMaintenance(id);
  getUserMaintenances();
  closeModal();
};

const showArchiveConfirm = ref(false);
const maintenanceToArchive = ref<IMaintenance | null>(null);
const isArchiveBusy = ref(false);

const openArchiveConfirm = (maintenance: IMaintenance) => {
  maintenanceToArchive.value = maintenance;
  showArchiveConfirm.value = true;
};

const cancelArchive = () => {
  showArchiveConfirm.value = false;
  maintenanceToArchive.value = null;
  isArchiveBusy.value = false;
};

const confirmArchiveMaintenance = async () => {
  if (!maintenanceToArchive.value || isArchiveBusy.value) return;

  isArchiveBusy.value = true;
  const maintenanceId = maintenanceToArchive.value.id;
  const archived = await maintenanceStore.archiveMaintenance(maintenanceId, { archived: true });
  isArchiveBusy.value = false;

  if (!archived) return;

  cancelArchive();
  if (selectedMaintenance.value?.id === maintenanceId) {
    closeModal();
  }
};

onMounted(() => {
  getUserMaintenances();
});
</script>

<template>
  <div class="container mx-auto p-4">
    <div class="grid grid-cols-1 gap-4 md:grid-cols-3 md:items-start">
      <MaintenanceSection
        :title="t('maintenance_user_listing.status.open')"
        :maintenances="maintenances.Open"
        section="open"
        border-color="border-orange-500"
        @select="selectMaintenance"
      />
      <MaintenanceSection
        :title="t('maintenance_user_listing.status.in_progress')"
        :maintenances="maintenances.InProgress"
        section="in_progress"
        border-color="border-blue-500"
        @select="selectMaintenance"
      />
      <MaintenanceSection
        :title="t('maintenance_user_listing.status.done')"
        :maintenances="maintenances.Finished"
        section="done"
        border-color="border-green-500"
        @select="selectMaintenance"
        @archive="openArchiveConfirm"
      />
    </div>

    <MaintenanceModal
      v-if="selectedMaintenance"
      :maintenance="selectedMaintenance"
      :section="selectedSection"
      :actions-taken="actionsTaken"
      :comments="comments"
      :new-comment-text="newCommentText"
      :editing-comment-id="editingCommentId"
      :current-tab="currentTab"
      :show-delete-confirm="showDeleteConfirm"
      :comments-limit="commentsLimit"
      @close="closeModal"
      @start="startMaintenance"
      @finish="finishedMaintenance"
      @destroy="handleDestroyMaintenance"
      @add-comment="addComment"
      @edit-comment="editComment"
      @start-editing="startEditingComment"
      @cancel-editing="cancelEditing"
      @confirm-delete-comment="confirmDeleteComment"
      @confirm-delete="confirmDelete"
      @cancel-delete="cancelDelete"
      @load-more-comments="loadMoreComments"
      @update:actionsTaken="value => actionsTaken = value"
      @update:newCommentText="value => newCommentText = value"
      @update:currentTab="value => currentTab = value"
      @maintenance-updated="onMaintenanceUpdated"
    />

    <ConfirmationModal
      :is-open="showArchiveConfirm"
      :cancel-text="t('maintenance_user_listing.modal.cancel')"
      :confirmation-text="
        isArchiveBusy
          ? t('maintenance_user_listing.modal.archive_confirming')
          : t('maintenance_user_listing.body.archive')
      "
      @cancel="cancelArchive"
      @add="confirmArchiveMaintenance"
    >
      <template #content>
        <div class="space-y-2">
          <h3 class="text-lg font-semibold text-black">
            {{ t('maintenance_user_listing.modal.archive_confirm_title') }}
          </h3>
          <p class="text-sm text-gray-600">
            {{ t('maintenance_user_listing.modal.archive_confirm_message') }}
          </p>
        </div>
      </template>
    </ConfirmationModal>
  </div>
</template>